// 期待迁移至 common-logic

import { isFinite } from "lodash";
import { IStatus, CellValue } from "@treelab/common-logic";
import { IReduxUser } from "./tableData_secondary";
import { FormulaResultType, ColumnType, ILookupTypeOptions, ITypeOptions, ISelect } from "../types/table-type";
import { convertLookupTypeOptions } from "../utils/filter_secondary";

// ================================ 辅助函数 ================================
interface IExhaustiveSwitchArgs {
  switchValue: never;
  returnValue?: any;
}
export const exhaustiveSwitchThrow = (({ switchValue }: Pick<IExhaustiveSwitchArgs, "switchValue">) => {
  throw new Error(`Received invalid switch value: \`${switchValue}\`.`);
}) as (args: IExhaustiveSwitchArgs) => never;

export const isColumnType = (arg: unknown): arg is ColumnType =>
  typeof arg === "string" && !!ColumnType[arg as ColumnType];

export const getFullName = (nickName: string) => {
  return nickName;
};

export type ArrayColumnType =
  | ColumnType.MULTI_SELECT
  | ColumnType.MULTI_ATTACHMENT
  | ColumnType.RECORD_REFERENCE
  | ColumnType.COLLABORATOR;

export const ARRAY_TYPES: {
  [key in ColumnType]: key extends ArrayColumnType ? true : false;
} = {
  [ColumnType.MULTI_ATTACHMENT]: true,
  [ColumnType.MULTI_SELECT]: true,
  [ColumnType.RECORD_REFERENCE]: true,
  [ColumnType.COLLABORATOR]: true,
  [ColumnType.NUMBER]: false,
  [ColumnType.TEXT]: false,
  [ColumnType.PHONE]: false,
  [ColumnType.EMAIL]: false,
  [ColumnType.DATETIME]: false,
  [ColumnType.SELECT]: false,
  [ColumnType.STATUS]: false,
  [ColumnType.CHECKBOX]: false,
  [ColumnType.RATING]: false,
  [ColumnType.LONG_TEXT]: false,
  [ColumnType.CURRENCY]: false,
  [ColumnType.AUTO_NUMBER]: false,
  [ColumnType.FORMULA]: false,
  [ColumnType.LOOKUP]: false,
  [ColumnType.NOOP]: false,
  [ColumnType.ROLLUP]: false,
  [ColumnType.UNIQUE_ID]: false,
  [ColumnType.CREATED_AT]: false,
  [ColumnType.CREATED_BY]: false,
};

export const isArrayType = (type: ColumnType): type is ArrayColumnType => !!ARRAY_TYPES[type];

export type AbsentCellValue = null | undefined;

export type MaybeAbsentCellValue<T extends ColumnType = ColumnType> = CellValue<T> | AbsentCellValue;

export const isAbsentCellValue = (cellValue: MaybeAbsentCellValue): cellValue is AbsentCellValue => {
  return cellValue === null || cellValue === undefined;
};

export const getCellValueAsArray = (value: unknown | unknown[]): unknown[] => {
  if (isAbsentCellValue(value as any)) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

// ================================ 辅助函数 ================================

type Ordered = string | number | boolean;

const collator = new Intl.Collator("zh-CN", { numeric: true, caseFirst: "lower" });

const compareOrdered = (valueA: Ordered, valueB: Ordered) => {
  return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
};

/**
 * Currently We sort text by the following rules:
 * 1、number(contain negative number)
 * 2、a-zA-Z
 * 3、symbols
 * 3、chinese pinyin
 * 4、others
 */
export const compareText = (stringA: string, stringB: string) => {
  if (!stringA && stringB) return -1;
  if (stringA && !stringB) return 1;
  /**
   * Begin with number or negative number or english character
   * include: 1, -1, -1a, a, A
   * exclude: -a
   */
  const isLetterOrNumberReg = /^((([0-9]\d*(\.\d+)?)+|((-[0-9]\d*(\.\d+)?)+$))|[a-z]+)/i;
  const isNegativeNumberReg = /^-[0-9]\d*(\.\d+)?$/i;
  if (isLetterOrNumberReg.test(stringA) && !isLetterOrNumberReg.test(stringB)) {
    return -1;
  }
  if (!isLetterOrNumberReg.test(stringA) && isLetterOrNumberReg.test(stringB)) {
    return 1;
  }
  /**
   * collator.compare can not handle negative number compare, It doesn't seem that minus signs are considered as part of numeric collation even under ICU, so we need to handle it manually
   */
  if (isNegativeNumberReg.test(stringA) && isNegativeNumberReg.test(stringB)) {
    return compareOrdered(Number(stringA), Number(stringB));
  }
  return collator.compare(stringA, stringB);
};

const compareNumber = (numberA: number, numberB: number) => {
  return compareOrdered(numberA, numberB);
};

const compareBoolean = (booleanA: boolean, booleanB: boolean) => {
  return compareOrdered(booleanA, booleanB);
};

export const compareDateString = (dateStringA: string, dateStringB: string) => {
  return compareNumber(dateStringA ? Date.parse(dateStringA) : 0, dateStringB ? Date.parse(dateStringB) : 0);
};

const compareOrderedColumn = (valueA: unknown, valueB: unknown) => {
  if (typeof valueA === "string" && typeof valueB === "string") {
    return compareText(valueA, valueB);
  }
  if (typeof valueA === "number" && typeof valueB === "number") {
    return compareNumber(valueA, valueB);
  }
  if (typeof valueA === "boolean" && typeof valueB === "boolean") {
    return compareBoolean(valueA, valueB);
  }
  throw new Error("Got non-ordered arguments!");
};

const compareSingleCollaborator = (
  getCollaboratorById: (groupId: string) => IReduxUser,
  groupIdA: string,
  groupIdB: string
) => {
  return compareText(
    getFullName(getCollaboratorById(groupIdA).nickName),
    getFullName(getCollaboratorById(groupIdB).nickName)
  );
};

const compareTextColumn = (valueA: unknown, valueB: unknown) => {
  if (typeof valueA !== "string" || typeof valueB !== "string") {
    throw new Error("Got non-string arguments!");
  }
  return compareText(valueA, valueB);
};

const compareNumberColumn = (a: unknown, b: unknown) => {
  const valueA = typeof a === "number" ? a : Number(a);
  const valueB = typeof b === "number" ? b : Number(b);
  if (!isFinite(valueA) && !isFinite(valueB)) return 0;
  if (!isFinite(valueA)) return -1;
  if (!isFinite(valueB)) return 1;
  return compareNumber(valueA as number, valueB as number);
};

const compareBooleanColumn = (valueA: unknown, valueB: unknown) => {
  if (typeof valueA !== "boolean" || typeof valueB !== "boolean") {
    throw new Error("Got non-boolean arguments!");
  }
  return compareBoolean(valueA, valueB);
};

const compareDateStringColumn = (a: unknown, b: unknown) => {
  const valueA = typeof a === "string" ? a : String(a);
  const valueB = typeof b === "string" ? b : String(b);
  if (!isFinite(Date.parse(valueA)) && !isFinite(Date.parse(valueB))) return 0;
  if (!isFinite(Date.parse(valueA))) return -1;
  if (!isFinite(Date.parse(valueB))) return 1;
  return compareDateString(valueA, valueB);
};

interface IReference {
  visibleName?: string;
}
const compareSingleReference = (referenceA: IReference, referenceB: IReference) => {
  return compareText(referenceA.visibleName || "", referenceB.visibleName || "");
};

const compareSelectColumn = (options: ISelect[], valueA: unknown, valueB: unknown) => {
  const optionAIndex = options.findIndex((option) => option.optionId === valueA);
  const optionBIndex = options.findIndex((option) => option.optionId === valueB);
  // if ((valueA && optionAIndex === -1) || (valueB && optionBIndex === -1)) {
  //   throw new Error("Couldn't find option definition for given id");
  // }
  return compareNumber(optionAIndex, optionBIndex);
};

const compareStatusColumn = (statuses: IStatus[], valueA: unknown, valueB: unknown) => {
  const statusAIndex = statuses.findIndex((status) => status.statusId === valueA);
  const statusBIndex = statuses.findIndex((status) => status.statusId === valueB);
  if ((valueA && statusAIndex === -1) || (valueB && statusBIndex === -1)) {
    throw new Error("Couldn't find status definition for given id");
  }
  return compareNumber(statusAIndex, statusBIndex);
};

const compareSingleReferenceColumn = (valueA: unknown, valueB: unknown) => {
  if (typeof valueA !== "object" || !valueA || typeof valueB !== "object" || !valueB) {
    throw new Error("Got non-object arguments!");
  }
  return compareSingleReference(valueA, valueB);
};

const compareSingleReferenceColumnForGrouping = (valueA: unknown, valueB: unknown) => {
  if (typeof valueA !== "string" || !valueA || typeof valueB !== "string" || !valueB) {
    throw new Error("Got non-string arguments!");
  }
  return compareText(valueA === "nullify" ? "" : valueA, valueB === "nullify" ? "" : valueB);
};

const compareSingleCollaboratorColumn = (
  getCollaboratorById: (groupId: string) => IReduxUser,
  valueA: unknown,
  valueB: unknown
) => {
  if (typeof valueA !== "string" || typeof valueB !== "string") {
    throw new Error("Got non-string arguments!");
  }
  return compareSingleCollaborator(getCollaboratorById, valueA, valueB);
};

const compareFormulaColumn = (options: ITypeOptions<ColumnType.FORMULA>, valueA: unknown, valueB: unknown) => {
  switch (options.resultType) {
    case FormulaResultType.DATE:
      return compareDateStringColumn(valueA, valueB);
    default:
      return compareOrderedColumn(valueA, valueB);
  }
};

const compareArray = <T>(arrayA: T[], arrayB: T[], itemComparator: (itemA: T, itemB: T) => number) => {
  for (let index = 0; ; index += 1) {
    const itemA = arrayA[index];
    const itemB = arrayB[index];

    if (!itemA && !itemB) {
      return 0;
    }
    if (!itemA) {
      return -1;
    }
    if (!itemB) {
      return 1;
    }

    const itemComparison = itemComparator(itemA, itemB);

    if (itemComparison !== 0) {
      return itemComparison;
    }
  }
};

const compareArrayColumn = (
  valueA: unknown,
  valueB: unknown,
  itemComparator: (itemA: unknown, itemB: unknown) => number
) => {
  if (!Array.isArray(valueA) || !Array.isArray(valueB)) {
    console.error("Error", valueA, valueB);
    throw new Error("Got non array arguments!");
  }
  return compareArray(valueA, valueB, itemComparator);
};

const compareMultiSelectColumn = (options: ISelect[], valueA: unknown, valueB: unknown) => {
  return compareArrayColumn(valueA, valueB, compareSelectColumn.bind(null, options));
};

const compareRecordReferenceColumn = (valueA: unknown, valueB: unknown) => {
  return compareArrayColumn(valueA, valueB, compareSingleReferenceColumn);
};

const compareCollaboratorColumn = (
  getCollaboratorById: (groupId: string) => IReduxUser,
  valueA: unknown,
  valueB: unknown
) => {
  return compareArrayColumn(valueA, valueB, compareSingleCollaboratorColumn.bind(null, getCollaboratorById));
};

// Whenever you JSON stringify undefined it will automatically turn into null. We actually really dont need to check for undefined here
export const compareByColumn = (
  getCollaboratorById: (groupId: string) => IReduxUser,
  columnType: ColumnType,
  typeOptions: ITypeOptions,
  valueA: unknown,
  valueB: unknown
): number => {
  if (!valueA && !valueB) {
    return 0;
  }
  if (!valueA) {
    return -1;
  }
  if (!valueB) {
    return 1;
  }

  try {
    switch (columnType) {
      /***** text based columns *****/
      case ColumnType.EMAIL:
      case ColumnType.LONG_TEXT:
      case ColumnType.PHONE:
      case ColumnType.UNIQUE_ID:
      case ColumnType.TEXT:
        return compareTextColumn(valueA, valueB);
      /***** number based columns *****/
      case ColumnType.CURRENCY:
      case ColumnType.RATING:
      case ColumnType.NUMBER:
      case ColumnType.AUTO_NUMBER:
        return compareNumberColumn(valueA, valueB);
      /***** boolean based columns *****/
      case ColumnType.CHECKBOX:
        return compareBooleanColumn(valueA, valueB);
      /***** datetime based columns *****/
      case ColumnType.DATETIME:
      case ColumnType.CREATED_AT:
        return compareDateStringColumn(valueA, valueB);
      /***** formula based columns *****/
      case ColumnType.FORMULA:
      case ColumnType.ROLLUP:
        return compareFormulaColumn(typeOptions as ITypeOptions<ColumnType.FORMULA>, valueA, valueB);

      /***** special columns *****/
      case ColumnType.SELECT:
        return compareSelectColumn((typeOptions as ITypeOptions<ColumnType.SELECT>).options, valueA, valueB);
      case ColumnType.STATUS:
        return compareStatusColumn((typeOptions as ITypeOptions<ColumnType.STATUS>).statuses, valueA, valueB);
      case ColumnType.MULTI_SELECT:
        return compareMultiSelectColumn((typeOptions as ITypeOptions<ColumnType.MULTI_SELECT>).options, valueA, valueB);
      case ColumnType.RECORD_REFERENCE:
        return compareRecordReferenceColumn(valueA, valueB);
      case ColumnType.LOOKUP: {
        const lookupType = (typeOptions as ILookupTypeOptions).lookupColumnType;
        const lookupTypeOptions = convertLookupTypeOptions(typeOptions as ILookupTypeOptions) as ITypeOptions;

        if (!isColumnType(lookupType)) {
          throw new Error("Couldn't get lookup column type");
        }
        const isLookupArrayType = isArrayType(lookupType);
        const lookupCompare = (lookupValueA: unknown, lookupValueB: unknown) => {
          return compareByColumn(
            getCollaboratorById,
            lookupType,
            lookupTypeOptions,
            isLookupArrayType && !Array.isArray(lookupValueA) ? [lookupValueA] : lookupValueA,
            isLookupArrayType && !Array.isArray(lookupValueB) ? [lookupValueB] : lookupValueB
          );
        };

        return compareArrayColumn(valueA, valueB, lookupCompare);
      }
      case ColumnType.COLLABORATOR: {
        return compareCollaboratorColumn(getCollaboratorById, valueA, valueB);
      }
      case ColumnType.CREATED_BY: {
        return compareCollaboratorColumn(getCollaboratorById, getCellValueAsArray(valueA), getCellValueAsArray(valueB));
      }
      case ColumnType.MULTI_ATTACHMENT:
      case ColumnType.NOOP:
        throw new Error("This column type is not sortable");
      default:
        // return exhaustiveSwitchThrow({
        //   switchValue: columnType,
        // });
        throw new Error("找不到这个 columnType");
    }
  } catch (e) {
    // logError(e, {
    //   columnType,
    //   typeOptions,
    //   valueA,
    //   valueB,
    // });
    console.error(e, columnType, typeOptions, valueA, valueB);
    return 0;
  }
};

// [Derek] - Super dirty, but grouping will work differently because of LOOKUP and RECORD_REFERENCE. Refactor needs to be done.
// I only did this for speed purposes.
export const compareByColumnForGrouping = (
  getCollaboratorById: (groupId: string) => IReduxUser,
  columnType: ColumnType,
  typeOptions: ITypeOptions,
  valueA: unknown,
  valueB: unknown
): number => {
  if (!valueA && !valueB) {
    return 0;
  }
  if (!valueA) {
    return -1;
  }
  if (!valueB) {
    return 1;
  }

  try {
    switch (columnType) {
      /***** text based columns *****/
      case ColumnType.EMAIL:
      case ColumnType.LONG_TEXT:
      case ColumnType.PHONE:
      case ColumnType.UNIQUE_ID:
      case ColumnType.TEXT:
        return compareTextColumn(valueA, valueB);
      /***** number based columns *****/
      case ColumnType.CURRENCY:
      case ColumnType.RATING:
      case ColumnType.AUTO_NUMBER:
      case ColumnType.NUMBER:
        return compareNumberColumn(valueA, valueB);
      /***** boolean based columns *****/
      case ColumnType.CHECKBOX:
        return compareBooleanColumn(valueA, valueB);
      /***** datetime based columns *****/
      case ColumnType.DATETIME:
      case ColumnType.CREATED_AT:
        return compareDateStringColumn(valueA, valueB);
      /***** formula based columns *****/
      case ColumnType.FORMULA:
      case ColumnType.ROLLUP:
        return compareFormulaColumn(typeOptions as ITypeOptions<ColumnType.FORMULA>, valueA, valueB);

      /***** special columns *****/
      case ColumnType.SELECT:
        return compareSelectColumn((typeOptions as ITypeOptions<ColumnType.SELECT>).options, valueA, valueB);
      case ColumnType.STATUS:
        return compareStatusColumn((typeOptions as ITypeOptions<ColumnType.STATUS>).statuses, valueA, valueB);
      case ColumnType.MULTI_SELECT:
        return compareMultiSelectColumn((typeOptions as ITypeOptions<ColumnType.MULTI_SELECT>).options, valueA, valueB);
      case ColumnType.RECORD_REFERENCE:
        return compareArrayColumn(valueA, valueB, compareSingleReferenceColumnForGrouping);
      case ColumnType.LOOKUP: {
        const lookupType = (typeOptions as ILookupTypeOptions).lookupColumnType;
        const lookupTypeOptions = convertLookupTypeOptions(typeOptions as ILookupTypeOptions) as ITypeOptions;

        if (!isColumnType(lookupType)) {
          throw new Error("Couldn't get lookup column type");
        }
        const isLookupArrayType = isArrayType(lookupType);
        const lookupCompare = (lookupValueA: unknown, lookupValueB: unknown) => {
          return compareByColumnForGrouping(
            getCollaboratorById,
            lookupType,
            lookupTypeOptions,
            isLookupArrayType && !Array.isArray(lookupValueA) ? [lookupValueA] : lookupValueA,
            isLookupArrayType && !Array.isArray(lookupValueB) ? [lookupValueB] : lookupValueB
          );
        };

        return compareArrayColumn(valueA, valueB, lookupCompare);
      }
      case ColumnType.COLLABORATOR: {
        return compareCollaboratorColumn(getCollaboratorById, valueA, valueB);
      }
      case ColumnType.CREATED_BY: {
        return compareCollaboratorColumn(getCollaboratorById, getCellValueAsArray(valueA), getCellValueAsArray(valueB));
      }
      case ColumnType.MULTI_ATTACHMENT:
      case ColumnType.NOOP:
        throw new Error("This column type is not sortable");
      default:
        return exhaustiveSwitchThrow({
          switchValue: columnType,
        });
    }
  } catch (e) {
    // logError(e, {
    //   columnType,
    //   typeOptions,
    //   valueA,
    //   valueB,
    // });
    console.error(e, columnType, typeOptions, valueA, valueB);
    return 0;
  }
};
